// Project-level PR effort roll-up: time taken per report + a rolled-up total.
// Internal (PR) only — the backend returns empty for portal users.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Clock, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

const fmtMins = (m) => {
    if (m == null) return "—";
    const h = Math.floor(m / 60), r = m % 60;
    return h ? `${h}h ${r}m` : `${r}m`;
};

export default function ProjectTimeSummary({ projectId }) {
    const [data, setData] = useState(null);
    const navigate = useNavigate();

    useEffect(() => {
        let live = true;
        api.get(`/projects/${projectId}/time-summary`)
            .then((r) => { if (live) setData(r.data); })
            .catch(() => { });
        return () => { live = false; };
    }, [projectId]);

    if (!data || !data.count) return null;

    return (
        <div data-testid="project-time-summary" className="bg-white border rounded-2xl p-5" style={{ borderColor: "#E5E0DA" }}>
            <div className="flex items-center gap-2 mb-4">
                <span className="grid place-items-center w-8 h-8 rounded-lg" style={{ background: "#EAF1FB" }}>
                    <Clock size={16} style={{ color: "#2A5DB0" }} />
                </span>
                <div>
                    <h3 className="text-[15px] font-bold text-[#1F1F1F]">Consultant time on this project</h3>
                    <p className="text-[11.5px] text-[#9A968F]">{data.tracked_count} of {data.count} report{data.count > 1 ? "s" : ""} time-tracked</p>
                </div>
                <div className="ml-auto text-right">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-[#9A968F]">Total time</div>
                    <div className="text-[26px] font-bold leading-none text-[#1F1F1F]" data-testid="project-time-total">
                        {fmtMins(data.total_minutes)}
                    </div>
                </div>
            </div>

            <div className="space-y-1.5">
                {data.reports.map((r) => (
                    <button key={r.id} data-testid={`project-time-row-${r.id}`}
                        onClick={() => navigate(`/app/inspections/${r.id}`)}
                        className="w-full flex items-center gap-3 text-left px-2.5 py-2 rounded-lg hover:bg-[#F6F4F0]">
                        <div className="flex-1 min-w-0">
                            <div className="text-[13px] font-semibold text-[#1F1F1F] truncate">{r.title}</div>
                            <div className="text-[11px] text-[#9A968F] truncate">
                                {r.template_code}{r.filer ? ` · ${r.filer}` : ""}{r.date ? ` · ${r.date}` : ""}
                            </div>
                        </div>
                        <span className="text-[13px] font-bold shrink-0" style={{ color: r.minutes == null ? "#9A968F" : "#1F1F1F" }}>
                            {fmtMins(r.minutes)}
                        </span>
                        <ChevronRight size={15} className="text-[#C0BCB4] shrink-0" />
                    </button>
                ))}
            </div>
        </div>
    );
}
